import React, { useEffect, useRef, useState } from "react";
import { useAvatarRegistry } from "../../utils/AvatarContext";

const toSrc = (raw) => {
  if (!raw) return null;
  return /^(data:|https?:|blob:)/.test(raw) ? raw : `data:image/png;base64,${raw}`;
};

// `size`/`borderRadius` accept a plain number (treated as px, the common
// case for list/table thumbnails) or a CSS length string (e.g. "80vh", for
// callers like a zoomed lightbox that need to track the viewport instead of
// a fixed pixel value).
const toCssLength = (value) => (typeof value === "number" ? `${value}px` : value);

const AvatarThumb = ({ id, alt = "avatar", size = 40, borderRadius = 6, onClick }) => {
  const { getObserver, subscribe, getCached } = useAvatarRegistry();
  const elRef = useRef(null);
  const [raw, setRaw] = useState(() => getCached(id) ?? null);

  useEffect(() => {
    setRaw(getCached(id) ?? null);
    return subscribe(id, setRaw);
  }, [id, subscribe, getCached]);

  useEffect(() => {
    const el = elRef.current;
    if (!el || raw) return;
    const observer = getObserver();
    el.dataset.avatarId = id;
    observer.observe(el);
    return () => observer.unobserve(el);
  }, [id, raw, getObserver]);

  const src = toSrc(raw);
  const cssSize = toCssLength(size);
  const cssRadius = toCssLength(borderRadius);

  return (
    <div
      ref={elRef}
      onClick={onClick}
      style={{
        width: cssSize,
        height: cssSize,
        borderRadius: cssRadius,
        backgroundColor: src ? "transparent" : "var(--input-bg, #f3f3f5)",
        overflow: "hidden",
        cursor: onClick ? "pointer" : undefined,
      }}
    >
      {src ? (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          style={{
            width: cssSize,
            height: cssSize,
            borderRadius: cssRadius,
            objectFit: "cover",
          }}
        />
      ) : null}
    </div>
  );
};

export default AvatarThumb;
